import { useState, useEffect } from "react";
import { getQuestions } from "@/api/questionnaireQuestions";
import {patchCandidateQuestionnaireResponses} from "@/api/candidateQuestionnaireResponses.jsx";
import useMutateData from "@/hooks/useMutateData";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";

export default function CandidateQuestionnaire({ candidateId, questionnaireResponses = [], updateCandidateData }) {
    const [isEditing, setIsEditing] = useState(false);
    const [questionsData, setQuestionsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { mutate: sendQuestionnaire, loading: sending } = useMutateData();

    useEffect(() => {

        const fetchQuestions = async () => {
            try {
                const data = await getQuestions();
                setQuestionsData(data || []);
            } catch (err) {
                setError("Failed to load questions");
            } finally {
                setLoading(false);
            }
        };

        fetchQuestions();
    }, []);

    const questions = questionsData;

    if (loading) return <p className="p-6">Loading questionnaire questions...</p>;
    if (error) return <p className="p-6 text-red-600">Error: {error}</p>;

    const responseLookup = questionnaireResponses.reduce((acc, response) => {
        acc[response.questionnaireQuestion.id] = response.answerText || "N/A";
        return acc;
    }, {});

    const handleSendQuestionnaire = async () => {
        await sendQuestionnaire("GET", `admin/candidates/${candidateId}/send_questionnaire`);
    };

    return isEditing ? (
        <CandidateQuestionnaireEdit
            candidateId={candidateId}
            questions={questions}
            responses={responseLookup}
            onSave={(updatedCandidate) => {
                console.log('on save')
                updateCandidateData(updatedCandidate);
                setIsEditing(false);
            }}
            onCancel={() => setIsEditing(false)}
        />
    ) : (
        <CandidateQuestionnaireView
            questions={questions}
            responses={responseLookup}
            onEdit={() => setIsEditing(true)}
            onSend={handleSendQuestionnaire}
            sending={sending}
        />
    );
}

function CandidateQuestionnaireView({ questions, responses, onEdit, onSend, sending }) {
    return (
        <div className="p-4 border rounded-lg bg-white shadow-sm">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Questionnaire</h3>
                <div className="space-x-2">
                    <Button onClick={onEdit}>Edit</Button>
                    <Button onClick={onSend} disabled={sending} variant="outline" className="bg-blue-500 text-white">
                        {sending ? "Sending..." : "Send to Candidate"}
                    </Button>
                </div>
            </div>

            <ul className="divide-y divide-gray-300">
                {questions.map(({ id, questionText }) => (
                    <li key={id} className="flex justify-between items-center py-3 px-2 hover:bg-gray-100 rounded-md">
                        <span className="font-medium">{questionText}</span>
                        <span className="text-gray-700">{responses[id] || "N/A"}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function CandidateQuestionnaireEdit({ candidateId, questions, responses, onSave, onCancel }) {
    const [formData, setFormData] = useState(responses);
    const { mutate, loading, error } = useMutateData();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formattedResponses = Object.entries(formData).map(([questionId, answerText]) => ({
            questionnaire_question_id: parseInt(questionId),
            answer_text: answerText,
        }));

        const result = await patchCandidateQuestionnaireResponses(candidateId, {
            questionnaire: { responses: formattedResponses }
        });

        if (result) {
            onSave(result);

        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-4 border rounded-lg bg-white shadow-sm">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Edit Questionnaire</h3>
                <div className="space-x-2">
                    <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
                    <Button type="submit" disabled={loading}>{loading ? "Saving..." : "Save"}</Button>
                </div>
            </div>

            {error && <p className="text-red-600">{error}</p>}

            <div className="space-y-4">
                {questions.map(({ id, questionText }) => {
                    if (id === 18 && formData[17] !== "Yes") return null;

                    return (
                        <div key={id} className="flex flex-col">
                            <label className="font-medium">{questionText}</label>
                            {id === 18 ? (
                                <Input
                                    type="text"
                                    name={id}
                                    value={formData[id] || ""}
                                    onChange={handleChange}
                                    placeholder="Enter your answer here..."
                                    className="mt-2 p-2 border rounded"
                                />
                            ) : (
                                <div className="flex space-x-4">
                                    <label>
                                        <input
                                            type="radio"
                                            name={id}
                                            value="Yes"
                                            checked={formData[id] === "Yes"}
                                            onChange={handleChange}
                                            className="mr-1"
                                        />
                                        Yes
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name={id}
                                            value="No"
                                            checked={formData[id] === "No"}
                                            onChange={handleChange}
                                            className="mr-1"
                                        />
                                        No
                                    </label>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </form>
    );
}
